import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDebounce } from '@/hooks/useDebounce';
import { useOnClickOutside } from '@/modules/Search/hooks/useOnClickOutside';
import { SearchResult } from '@/modules/Search/types';
import { X } from 'lucide-react';

import DocumentPreviewModal from '@/components/common/DocumentPreviewModal';
import SvgMic from '@/components/common/Icons/MicIcon';
import SvgSearch from '@/components/common/Icons/Search';
import {
  ContentSearchType,
  useSearchContentsQuery,
} from '@/components/common/Providers/GraphqlProvider/generated';

import { getSearchResultHref, SEARCH_CATEGORIES } from '../../../utils';
import SearchCategorySelector from './SearchCategorySelector';
import SearchResults from './SearchResults';

export default function HeaderSearch() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<ContentSearchType[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<{
    id: string;
    title: string;
    type: string;
  } | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const { query: urlQuery, category: urlCategory } = router.query;
  const debouncedQuery = useDebounce(query, 500);

  useEffect(() => {
    if (urlQuery) setQuery(urlQuery as string);
    if (urlCategory) {
      const cats = Array.isArray(urlCategory) ? urlCategory : [urlCategory];
      setSelectedCategories(cats as ContentSearchType[]);
    } else {
      setSelectedCategories([]);
    }
  }, [urlQuery, urlCategory]);

  const searchCategoriesInput =
    selectedCategories.length > 0
      ? selectedCategories.map((selectedCategory) => ({ type: { _eq: selectedCategory } }))
      : [];

  const { data } = useSearchContentsQuery({
    variables: {
      includePagination: false,
      pagination: {
        skip: 0,
        take: 5,
      },
      where: {
        _and: [
          {
            status: {
              _in: ['active', 'published'],
            },
          },
          { _or: searchCategoriesInput },
          {
            _or: [
              { title: { _contains: debouncedQuery } },
              { description: { _contains: debouncedQuery } },
              { searchableMetadata: { _contains: debouncedQuery } },
            ],
          },
        ],
      },
    },
    skip: !debouncedQuery.trim(),
  });

  const searchResults: SearchResult[] = useMemo(() => {
    if (!data?.contents) return [];
    return data.contents.map((content) => {
      const category = SEARCH_CATEGORIES.find((cat) => cat.id === content.type);

      return {
        id: content.id,
        category: category!,
        title: content.title,
        description: content.description || '',
        slug: content.slug,
      };
    });
  }, [data]);

  const handleResultClick = (searchResult: SearchResult) => {
    const { id, title, category } = searchResult;

    if (category.action === 'OPEN') {
      setSelectedDocument({ id, title, type: category.id });
      setShowResults(false);
      return;
    }

    const navigationPath = getSearchResultHref(category.id, id, searchResult.slug);

    if (navigationPath) {
      router.push(navigationPath);
    }

    setShowResults(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(value.trim().length > 0);
  };

  const handleCategoryChange = (categories: ContentSearchType[]) => {
    setSelectedCategories(categories);
    if (router.pathname === '/buscar') {
      const newQuery: any = { ...router.query, category: categories, page: 1 };
      if (categories.length === 0) delete newQuery.category;
      router.push({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
    }
  };

  const handleInputFocus = () => {
    if (query.trim().length > 0) {
      setShowResults(true);
    }
  };

  const clearSearch = () => {
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    setQuery('');
    setShowResults(false);
  };

  const handleSearchSubmit = () => {
    const { query: _q, category: _c, ...otherParams } = router.query;
    const newQuery: Record<string, any> = { ...otherParams };

    if (query.trim()) {
      newQuery.query = query.trim();
    }
    if (selectedCategories.length > 0) {
      newQuery.category = selectedCategories;
    }

    router.push({ pathname: '/buscar', query: newQuery });
    setShowResults(false);
  };

  const handleDocumentClose = () => {
    setSelectedDocument(null);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
  };

  useOnClickOutside([inputContainerRef, resultsContainerRef], () => setShowResults(false));

  const handleVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Tu navegador no soporta búsqueda por voz');
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-ES';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery(transcript);
      setShowResults(transcript.trim().length > 0);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  return (
    <div className="relative mr-2 hidden flex-col md:flex">
      <div className="flex">
        <SearchCategorySelector value={selectedCategories} onChange={handleCategoryChange} />

        <div
          className="flex items-center rounded-l-3xl rounded-r-3xl border border-blueLight px-2 py-1 lg:rounded-l-none"
          ref={inputContainerRef}
        >
          <input
            ref={inputRef}
            id="header-site-search"
            name="site-search-query"
            type="text"
            inputMode="search"
            autoComplete="off"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearchSubmit();
              }
            }}
            className="mx-2 min-w-64 font-workSans text-sm outline-none placeholder:text-xs placeholder:text-dimGray"
            placeholder="Realiza búsqueda en la sede electrónica..."
          />

          <div
            className={`cursor-pointer transition-colors ${isListening ? 'animate-pulse text-red-500' : 'text-silver hover:text-dimGray'}`}
            onClick={handleVoiceSearch}
            title={isListening ? 'Detener' : 'Búsqueda por voz'}
          >
            <SvgMic className="size-4" />
          </div>

          {query.trim().length > 0 && (
            <button
              type="button"
              onClick={() => {
                clearSearch();
                inputRef.current?.focus();
              }}
              className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-silver transition-colors hover:bg-gray-100 hover:text-dimGray"
              aria-label="Borrar búsqueda"
              title="Borrar"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          <div
            className="ml-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-silver"
            onClick={handleSearchSubmit}
          >
            <SvgSearch width={24} height={24} className="fill-white" />
          </div>
        </div>
      </div>

      {showResults && (
        <div
          ref={resultsContainerRef}
          style={{ zIndex: 'var(--z-dropdown)' }}
          className="absolute right-0 top-full mt-2 w-full max-w-3xl"
        >
          <SearchResults
            results={searchResults}
            categories={selectedCategories}
            query={query}
            onResultClick={handleResultClick}
            onCategoryToggle={(categoryId) => {
              const isActive = selectedCategories.includes(categoryId);
              const newCategories = isActive
                ? selectedCategories.filter((c) => c !== categoryId)
                : [...selectedCategories, categoryId];
              handleCategoryChange(newCategories);
            }}
            otherQueryParams={(() => {
              const { query: _q, category: _c, ...rest } = router.query;
              return rest;
            })()}
          />
        </div>
      )}

      <DocumentPreviewModal
        documentId={selectedDocument?.id || null}
        docTitle={selectedDocument?.title}
        documentType={
          selectedDocument?.type as ContentSearchType.Document | ContentSearchType.Regulation
        }
        onClose={handleDocumentClose}
      />
    </div>
  );
}
